import { useEffect, useRef, useState } from "react";
import type { Bucket } from "./helpers";
import { addDays, toISODate } from "./helpers";
import type { FilterPillSpec } from "../../components/FilterPills";
import { SectionCardHeader } from "../../components/ChartCard";

export type QueryRangeDays = 30 | 90 | 180;

export type PageFilters = {
  rangeDays: QueryRangeDays;
  bucket: Bucket;
  graphStart: string;
  graphEnd: string;
};

const BUCKET_LABEL: Record<Bucket, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

export function pageFiltersToPills(f: PageFilters): FilterPillSpec[] {
  const pills: FilterPillSpec[] = [];
  pills.push({ kind: "range", label: `Last ${f.rangeDays} days` });
  pills.push({ kind: "bucket", label: BUCKET_LABEL[f.bucket] });

  // Compute the implicit query window (today minus rangeDays, through today)
  // and only emit a cohort pill when the graph window is tighter than that.
  const today = new Date();
  const queryStart = toISODate(addDays(today, -f.rangeDays));
  const queryEnd = toISODate(today);
  const narrowsStart = f.graphStart > queryStart;
  const narrowsEnd = f.graphEnd < queryEnd;
  if (narrowsStart || narrowsEnd) {
    pills.push({ kind: "cohort", label: `${f.graphStart} to ${f.graphEnd}` });
  }
  return pills;
}

export function tableFiltersToPills(
  f: TableFilters,
  options: { key: string; label: string }[],
  onClearSelection: () => void,
): FilterPillSpec[] {
  const pills: FilterPillSpec[] = [];
  if (f.paidOnly) pills.push({ kind: "mode", label: "Paid plans only" });
  if (f.selectedKey) {
    const opt = options.find((o) => o.key === f.selectedKey);
    pills.push({
      kind: "selection",
      label: opt ? opt.label : f.selectedKey,
      onClear: onClearSelection,
    });
  }
  return pills;
}

export function PageFiltersBar({
  filters, onChange, graphMin, graphMax,
}: {
  filters: PageFilters;
  onChange: (next: PageFilters) => void;
  graphMin: string;
  graphMax: string;
}) {
  const set = <K extends keyof PageFilters>(key: K, value: PageFilters[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <section className="filterbar">
      <span className="filterbar-label"><i className="ti ti-filter" />Filters</span>

      <NativeSelect
        label="Query range"
        value={String(filters.rangeDays)}
        onChange={(v) => set("rangeDays", Number(v) as QueryRangeDays)}
        options={[
          { value: "30",  label: "Last 30 days" },
          { value: "90",  label: "Last 90 days" },
          { value: "180", label: "Last 180 days" },
        ]}
      />

      <NativeSelect
        label="Bucket"
        value={filters.bucket}
        onChange={(v) => set("bucket", v as Bucket)}
        options={[
          { value: "daily",   label: "Daily" },
          { value: "weekly",  label: "Weekly" },
          { value: "monthly", label: "Monthly" },
        ]}
      />

      <NativeDateInput
        label="Graph from"
        value={filters.graphStart}
        min={graphMin}
        max={filters.graphEnd}
        onChange={(v) => set("graphStart", v)}
      />

      <NativeDateInput
        label="to"
        value={filters.graphEnd}
        min={filters.graphStart}
        max={graphMax}
        onChange={(v) => set("graphEnd", v)}
      />
    </section>
  );
}

const chipShell: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--bg-2)",
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  padding: "4px 10px",
  fontSize: 12,
};

const chipLabel: React.CSSProperties = {
  color: "var(--fg-3)",
  fontWeight: 500,
};

const chipControl: React.CSSProperties = {
  appearance: "auto",
  background: "transparent",
  border: "none",
  padding: 0,
  font: "inherit",
  color: "var(--fg-1)",
  fontWeight: 600,
  cursor: "pointer",
  outline: "none",
};

function NativeSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label style={chipShell}>
      <span style={chipLabel}>{label}:</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={chipControl}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function NativeDateInput({
  label, value, min, max, onChange,
}: {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (next: string) => void;
}) {
  return (
    <label style={chipShell}>
      <span style={chipLabel}>{label}:</span>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        style={chipControl}
      />
    </label>
  );
}

export type TableFilters = {
  paidOnly: boolean;
  selectedKey: string | null;
  country: string | null;
};

export function TableFilterBar({
  title, subtitle, filters, onChange, options, allLabel, pickerLabel, pagePills, countryOptions,
}: {
  title: string;
  subtitle?: string;
  filters: TableFilters;
  onChange: (next: TableFilters) => void;
  options: { key: string; label: string }[];
  allLabel: string;
  pickerLabel: string;
  pagePills?: FilterPillSpec[];
  countryOptions?: string[];
}) {
  const tablePills = tableFiltersToPills(filters, options, () =>
    onChange({ ...filters, selectedKey: null }),
  );
  const allPills = [...tablePills, ...(pagePills ?? [])];

  const headerExtra = (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
      <label className="filter-switch">
        <input
          type="checkbox"
          checked={filters.paidOnly}
          onChange={(e) => onChange({ ...filters, paidOnly: e.target.checked })}
        />
        <span className="track"><span className="thumb" /></span>
        <span className="label-text">Paid users only</span>
      </label>

      {countryOptions && (
        <NativeSelect
          label="Country"
          value={filters.country ?? ""}
          onChange={(v) => onChange({ ...filters, country: v || null })}
          options={[
            { value: "", label: "All countries" },
            ...countryOptions.map((c) => ({ value: c, label: c })),
          ]}
        />
      )}

      <SearchableSelect
        allLabel={allLabel}
        pickerLabel={pickerLabel}
        options={options}
        selectedKey={filters.selectedKey}
        onChange={(key) => onChange({ ...filters, selectedKey: key })}
      />
    </div>
  );

  return (
    <SectionCardHeader
      title={title}
      subtitle={subtitle}
      filterPills={allPills}
      headerExtra={headerExtra}
    />
  );
}

function SearchableSelect({
  allLabel, pickerLabel, options, selectedKey, onChange,
}: {
  allLabel: string;
  pickerLabel: string;
  options: { key: string; label: string }[];
  selectedKey: string | null;
  onChange: (key: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = selectedKey
    ? options.find((o) => o.key === selectedKey)
    : null;
  const display = selected ? selected.label : allLabel;

  const lower = query.trim().toLowerCase();
  const filtered = lower
    ? options.filter((o) => o.label.toLowerCase().includes(lower))
    : options;

  return (
    <span className="filter-chip month-multi" ref={ref} style={{ minWidth: 240 }}>
      <span className="chip-label">{pickerLabel}:</span>
      <span className="chip-value" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
        {display}
      </span>
      <i className="ti ti-chevron-down chevron" />
      <button
        type="button"
        className="chip-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      {open && (
        <div
          className="month-multi-popover"
          role="listbox"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: 260, maxHeight: 360 }}
        >
          <input
            type="text"
            placeholder={`Search ${pickerLabel.toLowerCase()}...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              fontSize: 12,
              border: "1px solid var(--border-default)",
              borderRadius: 4,
              marginBottom: 6,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
          <label className="row" onClick={() => { onChange(null); setOpen(false); setQuery(""); }}>
            <input type="checkbox" checked={selectedKey === null} readOnly />
            <span>{allLabel}</span>
          </label>
          <div className="divider" />
          {filtered.slice(0, 200).map((o) => (
            <label
              key={o.key}
              className="row"
              onClick={() => { onChange(o.key); setOpen(false); setQuery(""); }}
            >
              <input type="checkbox" checked={selectedKey === o.key} readOnly />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
            </label>
          ))}
          {filtered.length > 200 && (
            <div style={{ fontSize: 11, color: "var(--fg-3)", padding: "4px 10px" }}>
              Showing first 200 of {filtered.length} matches. Narrow your search.
            </div>
          )}
        </div>
      )}
    </span>
  );
}
